/**
 * createGuestSchema — SCRUM-255 (DS-018 §10).
 *
 * Zod schema for `POST /organizations/:slug/guests`. The single source of
 * truth for the create request shape (CLAUDE.md rule 3 — never redefine a
 * schema locally in the API).
 *
 * Validation split (deliberate, SCRUM-255):
 *   - This schema validates SHAPE + LENGTH bounds (→ HTTP 400). Bounds mirror
 *     the domain VOs (GuestName / GuestAddress / GuestNote / GuestTag) for
 *     defense-in-depth, so malformed input fails cleanly at the boundary
 *     instead of bubbling out of a VO as a generic Error (the SCRUM-223 /
 *     PR-116 H1/H2 lesson).
 *   - CONTACT FORMAT is owned by the domain, not here. `primaryPhone` is
 *     PERMISSIVE (a bounded raw string): the application `PhoneInputNormalizer`
 *     turns a clean 10-digit NANP number into E.164 before the strict `Phone`
 *     VO sees it (§2.8). Pinning strict E.164 here would WRONGLY reject a valid
 *     `4155550100`. `primaryEmail` does use the shared `EMAIL_PATTERN` (same
 *     pattern the `Email` VO enforces — no drift).
 *   - The CORE INVARIANT (at least one of name/email/phone) is owned by the
 *     `Guest` aggregate (`EmptyGuestError` → 422), NOT refined here — a single
 *     authority for that rule. An empty `{}` body therefore passes Zod and is
 *     rejected by the domain as a 422 (semantically-invalid guest), not a 400.
 *
 * `.strict()` rejects unknown keys so a client cannot smuggle fields past the
 * boundary (mirrors guest-facing-info.schema).
 */
import { z } from 'zod';
import { EMAIL_PATTERN, EMAIL_ERROR_MESSAGE } from '../policies/email.js';
// --- Bounds (mirror the domain VOs) ----------------------------------------
const NAME_PART_MAX = 200;
const EMAIL_MAX = 254; // RFC 5321 max addr length
const PHONE_RAW_MAX = 32; // permissive raw input; normalizer → strict E.164
const ADDRESS_LINE_MAX = 200;
const ADDRESS_CITY_MAX = 100;
const ADDRESS_STATE_MAX = 100;
const ADDRESS_POSTAL_MAX = 20;
const ADDRESS_COUNTRY_MAX = 100;
const NOTE_BODY_MAX = 2000;
const TAG_MAX = 50;
const TAGS_ARRAY_MAX = 50;
const NOTES_ARRAY_MAX = 100;
// --- Reusable field schemas (also consumed by update-guest.schema.ts) -------
/**
 * Structured name. Each part, WHEN PRESENT, must be a non-empty bounded
 * string; `null` clears that part. The "≥1 part yields a non-empty display
 * name" rule is the `GuestName` VO's (→ 422), not Zod's.
 */
export const guestNameSchema = z
    .object({
    given: z.string().min(1).max(NAME_PART_MAX).nullable().optional(),
    family: z.string().min(1).max(NAME_PART_MAX).nullable().optional(),
    override: z.string().min(1).max(NAME_PART_MAX).nullable().optional(),
})
    .strict();
export const guestEmailSchema = z
    .string()
    .max(EMAIL_MAX)
    .regex(EMAIL_PATTERN, EMAIL_ERROR_MESSAGE);
/** Permissive raw phone — strict E.164 is enforced downstream (§2.8). */
export const guestPhoneSchema = z.string().min(1).max(PHONE_RAW_MAX);
export const guestAddressSchema = z
    .object({
    line1: z.string().min(1).max(ADDRESS_LINE_MAX),
    line2: z.string().max(ADDRESS_LINE_MAX).nullable().optional(),
    city: z.string().min(1).max(ADDRESS_CITY_MAX),
    stateProvince: z.string().max(ADDRESS_STATE_MAX).nullable().optional(),
    postalCode: z.string().max(ADDRESS_POSTAL_MAX).nullable().optional(),
    country: z.string().min(1).max(ADDRESS_COUNTRY_MAX),
})
    .strict();
export const guestNoteSchema = z
    .object({ body: z.string().min(1).max(NOTE_BODY_MAX) })
    .strict();
export const guestTagSchema = z.string().min(1).max(TAG_MAX);
export const guestTagsSchema = z.array(guestTagSchema).max(TAGS_ARRAY_MAX);
export const guestNotesSchema = z.array(guestNoteSchema).max(NOTES_ARRAY_MAX);
// --- Create request ---------------------------------------------------------
export const createGuestSchema = z
    .object({
    name: guestNameSchema.optional(),
    primaryEmail: guestEmailSchema.nullable().optional(),
    primaryPhone: guestPhoneSchema.nullable().optional(),
    address: guestAddressSchema.nullable().optional(),
    notes: guestNotesSchema.optional(),
    tags: guestTagsSchema.optional(),
})
    .strict();
//# sourceMappingURL=create-guest.schema.js.map